import React, { ReactNode, useRef, useEffect } from "react";

export interface FilterField {
  id: string;
  label: string;
  type: "select" | "date" | "text" | "custom";
  value: string | string[];
  onChange: (value: string | string[]) => void;
  options?: { value: string; label: string }[];
  placeholder?: string;
  customContent?: ReactNode;
  multiple?: boolean;
}

interface RecordsFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  fields: FilterField[];
  activeCount?: number;
  onClearAll?: () => void;
  clearAllLabel?: string;
}

export const RecordsFilters: React.FC<RecordsFiltersProps> = ({
  isOpen,
  onClose,
  title = "Filters",
  fields,
  activeCount = 0,
  onClearAll,
  clearAllLabel = "Clear all",
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is on the filters toggle button
      const filterButton = (target as Element).closest?.('[data-filters-toggle-button]');
      
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        !filterButton
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50"
    >
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          {activeCount > 0 && onClearAll && (
            <button
              onClick={onClearAll}
              className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
            >
              {clearAllLabel}
            </button>
          )}
        </div>

        {/* Filter Fields */}
        {fields.map((field) => (
          <div key={field.id}>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {field.label}
            </label>

            {field.type === "select" && (
              <select
                value={Array.isArray(field.value) ? field.value[0] || "" : field.value}
                onChange={(e) => field.onChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {field.placeholder && (
                  <option value="">{field.placeholder}</option>
                )}
                {field.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}

            {field.type === "date" && (
              <input
                type="date"
                value={Array.isArray(field.value) ? field.value[0] || "" : field.value}
                onChange={(e) => field.onChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            )}

            {field.type === "text" && (
              <input
                type="text"
                value={Array.isArray(field.value) ? field.value[0] || "" : field.value}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder={field.placeholder}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            )}

            {field.type === "custom" && field.customContent}
          </div>
        ))}
      </div>
    </div>
  );
};
